/**
 * Public + admin API wrappers for Phase 4 Reviews.
 *
 * Public endpoints (no cookie): submitReview, uploadReviewPhotos,
 *   listPublicReviews, getPublicReview.
 * Admin endpoints (require the `act_admin` cookie the ConciergeLogin
 *   flow sets): listAdminReviews, moderateReview, deleteReview,
 *   replyReview.
 *
 * All calls use `REACT_APP_BACKEND_URL` and go through the /api ingress.
 */

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function _json(res) {
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return null;
    return res.json();
}

async function _handle(res, defaultMsg) {
    if (res.ok) return (await _json(res)) ?? {};
    let detail = defaultMsg;
    const body = await _json(res);
    if (body) {
        if (typeof body.detail === "string") detail = body.detail;
        else if (Array.isArray(body.detail) && body.detail.length) {
            const first = body.detail[0];
            detail = first?.msg || defaultMsg;
        }
    }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
}

export const reviewsApi = {
    /** POST /api/reviews — submit a new review (returns {id, status}). */
    async submitReview(payload) {
        const res = await fetch(`${API_BASE}/reviews`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        return _handle(res, "Failed to submit review.");
    },

    /** POST /api/reviews/{id}/photos — multipart upload (1-3 files). */
    async uploadReviewPhotos(reviewId, files) {
        const fd = new FormData();
        for (const f of files) fd.append("files", f, f.name);
        const res = await fetch(`${API_BASE}/reviews/${reviewId}/photos`, {
            method: "POST",
            body: fd,
        });
        return _handle(res, "Photo upload failed.");
    },

    /** GET /api/reviews?page=&per_page= — paginated approved reviews. */
    async listPublicReviews({ page = 1, perPage = 10 } = {}) {
        const url = `${API_BASE}/reviews?page=${page}&per_page=${perPage}`;
        const res = await fetch(url);
        return _handle(res, "Failed to load reviews.");
    },

    /** GET /api/reviews/{id} — a single approved review. */
    async getPublicReview(id) {
        const res = await fetch(`${API_BASE}/reviews/${id}`);
        return _handle(res, "Review not found.");
    },

    // ----- ADMIN -----------------------------------------------------------
    async listAdminReviews({ status = "pending", page = 1, perPage = 20 } = {}) {
        const url = `${API_BASE}/admin/reviews?status=${status}&page=${page}&per_page=${perPage}`;
        const res = await fetch(url, { credentials: "include" });
        return _handle(res, "Failed to load reviews.");
    },
    async moderateReview(id, status) {
        const res = await fetch(`${API_BASE}/admin/reviews/${id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ status }),
        });
        return _handle(res, "Moderation failed.");
    },
    async deleteReview(id) {
        const res = await fetch(`${API_BASE}/admin/reviews/${id}`, {
            method: "DELETE",
            credentials: "include",
        });
        return _handle(res, "Delete failed.");
    },
    async replyReview(id, { channel, message }) {
        const res = await fetch(`${API_BASE}/admin/reviews/${id}/reply`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ channel, message }),
        });
        return _handle(res, "Reply failed.");
    },
};

/** Absolute URL for a review photo stored at `/uploads/reviews/…`. */
export function reviewPhotoUrl(relative) {
    if (!relative) return "";
    if (relative.startsWith("http")) return relative;
    return `${process.env.REACT_APP_BACKEND_URL}${relative}`;
}
